import { type ReactNode, useState } from 'react';

import {
  RAISE_NOT_AUTHORISED,
  UPSTREAM_BELOW_CLIENT_LIMIT,
  UPSTREAM_BELOW_PLATFORM_BID,
} from '@carsfinders/contracts';

import { ConfirmDialog } from '../../../components/confirm-dialog';
import { ApiError } from '../../../lib/api';
import { t } from '../../../lib/i18n';
import { toast } from '../../../lib/toast';
import { useSessionUser } from '../../clients/hooks/use-session-user';
import { useRelayAction, useRelayClaim } from '../hooks/use-bid-mutations';
import {
  type ConfirmRelayPayload,
  RELAY_CLAIM_REQUIRED_ACTIONS,
  RELAY_CLAIMABLE_STATES,
  RELAY_REJECT_REASONS,
  RELAY_TRANSITIONS,
  type RejectRelayPayload,
  type RelayAction,
  type RelayClaimAction,
  type RelayQueueItem,
  type RelayRejectReason,
} from '../types';
import { cn, formatAmount, formatClock, formatDayMonth } from '../utils';
import { RelaySlaDot } from './relay-sla-dot';
import { RelayStatusBadge } from './relay-status-badge';

/** `Лот 1.4fr · Клієнт 1.4fr · Сума · Статус · SLA · Закриття · Дії` at 1040px. */
const GRID =
  'lg:grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1.2fr)_110px_130px_130px_110px_minmax(0,1fr)]';

const COLUMNS = [
  { key: 'lot', align: '' },
  { key: 'client', align: '' },
  { key: 'amount', align: 'text-right' },
  { key: 'status', align: '' },
  { key: 'sla', align: '' },
  { key: 'close', align: '' },
  { key: 'actions', align: 'text-right' },
] as const;

/**
 * The lot label for a queue row.
 *
 * `brand` + `model` and nothing else, deliberately. The new-bid TOAST describes
 * the same bid on the same screen, and its label is built server-side by
 * `getCarInfo` in `apps/api/.../bid-change-stream.service.ts` — which reads
 * these same two fields for exactly this reason. Change one, change the other,
 * or an operator sees the lot named two ways at once (they did: the toast said
 * "Unknown Model" for every lot until 2026-09-21).
 *
 * Not `ai_brand`: that is an alias the cars aggregation produces
 * (`aggregate-car.ts` — `ai_brand: alpicarCar.brand || alpicarCar.make`), so it
 * exists on the display type and not on the `alpicar_cars` document this row's
 * API projects from. (Other collections have code that assumes it IS stored —
 * see the note on `getCarInfo` in the api; unverified, and not what this reads.)
 * Not `make` either: it is always present but differs from `brand` on roughly
 * half the collection, so it is a different value, not a fallback.
 */
function carLabel(item: RelayQueueItem): string {
  const car = item.car;
  const name = `${car?.brand ?? ''} ${car?.model ?? ''}`.trim();
  return name || car?.listing_id || item.carId;
}

/** The three fields of the confirm-relay form, as typed (strings — never coerced before submit). */
export interface ConfirmRelayForm {
  upstreamRef: string;
  upstreamAmount: string;
  upstreamPlacedAt: string;
}

/**
 * Build the confirm-relay body from the form — EXACTLY the vocabulary the API's
 * zod `ConfirmRelayBodySchema` validates (`upstreamRef`, optional
 * `upstreamAmount` number, optional `upstreamPlacedAt` ISO string). Null when
 * the reference is empty: confirming a relay we cannot point at is worse than
 * not confirming. The phase-1 desk sent free text and every save 400ed; this
 * is the one place the wire shape is decided, and it is unit-tested.
 */
export function buildConfirmRelayPayload(form: ConfirmRelayForm): ConfirmRelayPayload | null {
  const upstreamRef = form.upstreamRef.trim();
  if (!upstreamRef) return null;

  const payload: ConfirmRelayPayload = { upstreamRef };

  const amountText = form.upstreamAmount.replace(/\s/g, '');
  if (amountText !== '') {
    const amount = Number(amountText);
    if (Number.isFinite(amount) && amount > 0) payload.upstreamAmount = amount;
  }

  if (form.upstreamPlacedAt.trim() !== '') {
    // `<input type="datetime-local">` yields local wall time; the API wants an instant.
    const placedAt = new Date(form.upstreamPlacedAt);
    if (!Number.isNaN(placedAt.getTime())) payload.upstreamPlacedAt = placedAt.toISOString();
  }

  return payload;
}

/** The API's own refusal vocabulary → the operator's wording; anything else is the generic failure. */
export function relayErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const code = (error.body as { code?: string } | null | undefined)?.code;
    switch (code) {
      case 'CLAIM_REQUIRED':
        return t('bids.relayQueue.claim.required');
      case 'ALREADY_CLAIMED':
        return t('bids.relayQueue.claim.alreadyClaimed');
      case 'NOT_CLAIMABLE':
        return t('bids.relayQueue.claim.notClaimable');
      case UPSTREAM_BELOW_PLATFORM_BID:
        return t('bids.relayQueue.errors.upstreamBelowPlatformBid');
      case RAISE_NOT_AUTHORISED:
        return t('bids.relayQueue.errors.raiseNotAuthorised');
      case 'LOT_CLOSED':
        return t('bids.relayQueue.errors.lotClosed');
      default:
        break;
    }
  }
  return t('bids.relayQueue.actionError');
}

/**
 * «У клієнта автоставка…» — a confirm-relay refusal the DIALOG answers itself
 * (Phase 15): the owner's own auto-bid limit L and the raise the API suggests.
 * `RAISE_NOT_AUTHORISED` counts only while it still carries a usable limit
 * (the lot moved, or the operator typed above L) — then the raise step stays
 * open with the fresh numbers. Null for everything else: that is the caller's
 * refusal to toast.
 */
export function upstreamLimitRefusal(
  error: unknown,
): { clientLimitChf: number; suggestedChf: number; again: boolean } | null {
  if (!(error instanceof ApiError)) return null;
  const body = (error.body ?? {}) as {
    code?: string;
    clientLimitChf?: unknown;
    suggestedChf?: unknown;
  };
  if (body.code !== UPSTREAM_BELOW_CLIENT_LIMIT && body.code !== RAISE_NOT_AUTHORISED) return null;
  if (typeof body.clientLimitChf !== 'number' || typeof body.suggestedChf !== 'number') return null;
  return {
    clientLimitChf: body.clientLimitChf,
    suggestedChf: body.suggestedChf,
    again: body.code === RAISE_NOT_AUTHORISED,
  };
}

/** The success toast for a confirm-relay answer — the upstream decisions say what happened. */
export function confirmRelaySuccessMessage(result: unknown): string {
  const body = (result ?? {}) as {
    decision?: string;
    upstreamHigherChf?: number;
    upstreamAmount?: number;
  };
  if (body.decision === 'outbid' && typeof body.upstreamHigherChf === 'number') {
    return t('bids.relayQueue.upstream.outbidDone', {
      amount: formatAmount(body.upstreamHigherChf),
    });
  }
  if (body.decision === 'raise' && typeof body.upstreamAmount === 'number') {
    return t('bids.relayQueue.upstream.raiseDone', { amount: formatAmount(body.upstreamAmount) });
  }
  return t('bids.relayQueue.actionSuccess');
}

/** "15 000" / "15000" / "15'000" → 15000; null for anything that is not a positive amount. */
export function parseChf(text: string): number | null {
  const cleaned = text.replace(/[\s'’]/g, '');
  if (!/^\d+(\.\d+)?$/.test(cleaned)) return null;
  const value = Number(cleaned);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * Relay desk queue — `Redesign-D-Admin`, wearing the «Ставки» chrome.
 *
 * The lifecycle verbs and the transition matrix that gates them are untouched:
 * `RELAY_TRANSITIONS` still decides which buttons a row offers. Phase 13 adds
 * the operator CLAIM (a work lock — «у роботі: …» — so two operators never
 * place the same bid twice), replaces the browser prompt with a form that
 * emits the API's exact confirm-relay DTO, and adds the «відхилено аукціоном»
 * outcome with its reason vocabulary. This is the screen where a client's bid
 * becomes a real bid on a real Swiss auction, so every new control speaks the
 * server's words and nothing else.
 */
export function RelayQueueTable({ items }: { items: RelayQueueItem[] }) {
  if (items.length === 0) {
    return (
      <section className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-10 text-center">
        <p className="text-base font-semibold">{t('bids.relayQueue.empty')}</p>
      </section>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-2 lg:hidden">
        {items.map((item) => (
          <RelayRow key={item.bidId} item={item} />
        ))}
      </div>

      <section className="hidden overflow-x-auto rounded-cf-lg border border-cf-line bg-cf-surface lg:block">
        <div
          className={cn(
            GRID,
            'min-w-[1040px] gap-3.5 border-b border-cf-line-soft px-[18px] py-2.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase',
          )}
        >
          {COLUMNS.map((column) => (
            <span key={column.key} className={column.align}>
              {t(`bids.dAdmin.relayDesk.columns.${column.key}`)}
            </span>
          ))}
        </div>

        {items.map((item) => (
          <RelayRow key={item.bidId} item={item} />
        ))}
      </section>
    </>
  );
}

type RowDialog = 'confirm' | 'reject' | null;

/**
 * What the operator controls need to know about a bid — a queue row carries
 * all of it; the bid detail page builds it from `GET /bids/:id`.
 */
export type RelayControlTarget = Pick<
  RelayQueueItem,
  'bidId' | 'bid' | 'relayStatus' | 'claimedBy' | 'claimedByLabel' | 'claimedAt'
>;

/** The three fragments {@link useRelayControls} hands back, placed by the caller. */
export interface RelayControls {
  /** «у роботі: ви / <masked>» — null when nobody holds the row. */
  claimBadge: ReactNode;
  /** The claim / release / transition buttons, claim-gated. */
  actions: ReactNode;
  /** The confirm-relay and reject dialogs, mounted only while open. */
  dialogs: ReactNode;
}

/**
 * THE operator controls for one bid — claim / release, queue, confirm-relay,
 * outcome, reject, cancel — with the claim gate and the API's error wording.
 * Shared by the queue row and the bid detail page (review I-3): the queue
 * lists `captured|queued` only, so a `relayed` bid — the one whose reject /
 * outcome is the phase's central case — is reachable ONLY from the detail
 * page, and it must offer the same buttons, gated the same way, not a fork.
 */
export function useRelayControls(item: RelayControlTarget): RelayControls {
  const relayMutation = useRelayAction();
  const claimMutation = useRelayClaim();
  const { user: self } = useSessionUser();
  const [dialog, setDialog] = useState<RowDialog>(null);

  const busy = relayMutation.isPending || claimMutation.isPending;
  const selfId = self?._id;
  const claimedBy = item.claimedBy ?? null;
  const isMine = Boolean(claimedBy && selfId && claimedBy === selfId);
  const claimable = RELAY_CLAIMABLE_STATES.includes(item.relayStatus);

  const can = (action: RelayAction): boolean =>
    RELAY_TRANSITIONS[action].includes(item.relayStatus);

  /**
   * Claim-gated verbs stay VISIBLE but disabled until this operator holds the
   * row — the API answers 403 CLAIM_REQUIRED otherwise, and a button that
   * always fails explains less than one that says "take it first".
   */
  const gated = (action: RelayAction): boolean =>
    RELAY_CLAIM_REQUIRED_ACTIONS.includes(action) && !isMine;

  /**
   * confirm-relay has its own handler (Phase 15): an upstream decision says
   * what happened in its toast, and the «у клієнта автоставка» refusal is
   * handed BACK to the dialog (rethrown) — it is the dialog's next step, not
   * a failure.
   */
  const confirm = async (payload: ConfirmRelayPayload) => {
    if (busy) return;
    try {
      const result = await relayMutation.mutateAsync({
        id: item.bidId,
        action: 'confirm-relay',
        body: { ...payload },
      });
      setDialog(null);
      toast.success(confirmRelaySuccessMessage(result));
    } catch (error) {
      if (upstreamLimitRefusal(error)) throw error;
      toast.error(relayErrorMessage(error));
    }
  };

  const run = async (action: RelayAction, body?: Record<string, unknown>) => {
    if (busy) return;
    try {
      await relayMutation.mutateAsync({ id: item.bidId, action, body });
      setDialog(null);
      toast.success(t('bids.relayQueue.actionSuccess'));
    } catch (error) {
      toast.error(relayErrorMessage(error));
    }
  };

  const claim = async (action: RelayClaimAction) => {
    if (busy) return;
    try {
      await claimMutation.mutateAsync({ id: item.bidId, action });
      toast.success(
        t(
          action === 'claim'
            ? 'bids.relayQueue.claim.claimSuccess'
            : 'bids.relayQueue.claim.releaseSuccess',
        ),
      );
    } catch (error) {
      toast.error(relayErrorMessage(error));
    }
  };

  // Resolved to a literal first: an undefined param makes `t` echo `{who}`.
  const claimLabel = claimedBy
    ? isMine
      ? t('bids.relayQueue.claim.mine')
      : t('bids.relayQueue.claim.other', {
          who: item.claimedByLabel || claimedBy,
        })
    : null;

  const claimBadge = claimLabel ? (
    <span
      data-testid="claim-badge"
      title={
        item.claimedAt
          ? `${formatDayMonth(item.claimedAt)} ${formatClock(item.claimedAt)}`
          : undefined
      }
      className={cn(
        'inline-flex w-fit items-center rounded-cf-sm px-[7px] py-[3px] font-mono text-[9px] tracking-[0.08em] whitespace-nowrap uppercase lg:px-2 lg:py-1 lg:text-[10px]',
        isMine ? 'bg-cf-success-tint text-cf-success' : 'bg-cf-warn-tint text-cf-warn',
      )}
    >
      {claimLabel}
    </span>
  ) : null;

  const actions = (
    <>
      {claimable && !isMine && (
        <ActionButton
          onClick={() => void claim('claim')}
          disabled={busy}
          variant="outline"
          label={t('bids.relayQueue.actions.claim')}
        />
      )}
      {isMine && (
        <ActionButton
          onClick={() => void claim('release')}
          disabled={busy}
          variant="outline"
          label={t('bids.relayQueue.actions.release')}
        />
      )}
      {can('queue') && (
        <ActionButton
          onClick={() => void run('queue')}
          disabled={busy}
          variant="primary"
          label={t('bids.relayQueue.actions.queue')}
        />
      )}
      {can('confirm-relay') && (
        <ActionButton
          onClick={() => setDialog('confirm')}
          disabled={busy || gated('confirm-relay')}
          title={gated('confirm-relay') ? t('bids.relayQueue.claim.required') : undefined}
          variant="primary"
          label={t('bids.relayQueue.actions.confirmRelay')}
        />
      )}
      {/* `outcome` is legal from `relayed` only. The queue lists captured|queued
          rows, so on the desk these never render; they are live on the bid
          detail page, which mounts these same controls for a relayed bid. */}
      {can('outcome') && (
        <>
          <ActionButton
            onClick={() => void run('outcome', { outcome: 'won' })}
            disabled={busy || gated('outcome')}
            title={gated('outcome') ? t('bids.relayQueue.claim.required') : undefined}
            variant="success"
            label={t('bids.relayQueue.actions.markWon')}
          />
          <ActionButton
            onClick={() => void run('outcome', { outcome: 'lost' })}
            disabled={busy || gated('outcome')}
            title={gated('outcome') ? t('bids.relayQueue.claim.required') : undefined}
            variant="danger"
            label={t('bids.relayQueue.actions.markLost')}
          />
        </>
      )}
      {can('reject') && (
        <ActionButton
          onClick={() => setDialog('reject')}
          disabled={busy || gated('reject')}
          title={gated('reject') ? t('bids.relayQueue.claim.required') : undefined}
          variant="danger"
          label={t('bids.relayQueue.actions.reject')}
        />
      )}
      {can('cancel-relay') && (
        <ActionButton
          onClick={() => void run('cancel-relay')}
          disabled={busy}
          variant="outline"
          label={t('bids.relayQueue.actions.cancel')}
        />
      )}
    </>
  );

  // Mounted only while open, so every opening starts from a clean form.
  const dialogs = (
    <>
      {dialog === 'confirm' && (
        <ConfirmRelayDialog
          item={item}
          busy={busy}
          onCancel={() => setDialog(null)}
          onSubmit={confirm}
        />
      )}
      {dialog === 'reject' && (
        <RejectRelayDialog
          busy={busy}
          onCancel={() => setDialog(null)}
          onSubmit={(payload) => void run('reject', { ...payload })}
        />
      )}
    </>
  );

  return { claimBadge, actions, dialogs };
}

function RelayRow({ item }: { item: RelayQueueItem }) {
  const { claimBadge, actions, dialogs } = useRelayControls(item);

  const closeLabel = item.tenderCloseAt
    ? `${formatDayMonth(item.tenderCloseAt)} ${formatClock(item.tenderCloseAt)}`.trim()
    : '—';

  return (
    <>
      {/* ── Mobile card ─────────────────────────────────────────────────── */}
      {/*
        `data-testid` + `data-listing-id` + `data-bid-id` are a TEST SEAM, on
        both responsive copies deliberately. The queue is a CSS grid, not a
        table — there is no <tr> to address a row by — and the visible label is
        `carLabel` (brand + model), which is neither unique nor the lot name a
        fixture seeds. The e2e journey used to hunt for `tr` by the seeded title
        and matched nothing at all, silently.
      */}
      <article
        data-testid="relay-queue-row"
        data-bid-id={item.bidId}
        data-listing-id={item.car?.listing_id ?? item.carId}
        className="flex flex-col gap-2 rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-3 lg:hidden"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">{carLabel(item)}</div>
            <div className="truncate text-xs text-cf-ink-subtle">
              {item.user?.email ?? item.userId}
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div className="font-mono text-[15px] font-medium whitespace-nowrap">
              CHF {formatAmount(item.bid)}
            </div>
            <div className="font-mono text-[11px] text-cf-ink-subtle">{closeLabel}</div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <RelayStatusBadge relayStatus={item.relayStatus} />
          <RelaySlaDot severity={item.slaSeverity} />
          {claimBadge}
        </div>

        <div className="flex flex-wrap justify-end gap-1.5">{actions}</div>
      </article>

      {/* ── Desktop row ─────────────────────────────────────────────────── */}
      <div
        data-testid="relay-queue-row"
        data-bid-id={item.bidId}
        data-listing-id={item.car?.listing_id ?? item.carId}
        className={cn(
          GRID,
          'hidden min-w-[1040px] items-center gap-3.5 border-b border-cf-line-soft px-[18px] py-3 text-[13px] last:border-b-0 hover:bg-cf-surface-sunken lg:grid',
        )}
      >
        <div className="min-w-0">
          <div className="truncate font-semibold">{carLabel(item)}</div>
          <div className="truncate font-mono text-xs text-cf-ink-subtle">
            {item.car?.listing_id ?? item.carId}
          </div>
        </div>

        <div className="min-w-0 truncate text-cf-ink-muted">{item.user?.email ?? item.userId}</div>

        <span className="text-right font-mono text-sm font-medium whitespace-nowrap">
          CHF {formatAmount(item.bid)}
        </span>

        <div className="flex flex-col items-start gap-1">
          <RelayStatusBadge relayStatus={item.relayStatus} />
          {claimBadge}
        </div>
        <RelaySlaDot severity={item.slaSeverity} />

        <span className="font-mono text-xs text-cf-ink-muted">{closeLabel}</span>

        <div className="flex flex-wrap items-center justify-end gap-1.5">{actions}</div>
      </div>

      {dialogs}
    </>
  );
}

const FIELD_CLASS =
  'mt-1 block w-full rounded-cf-md border border-cf-line bg-cf-surface px-3 py-2 text-sm text-cf-ink';

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-xs font-semibold text-cf-ink-muted">
      {label}
      {children}
    </label>
  );
}

/**
 * Upstream fields (reference / amount / time) on the confirm-relay dialog.
 * Hidden by owner decision 2026-09-28: the dialog is a plain «Підтвердити
 * передачу / Скасувати» and sends an empty body (the API no longer requires
 * `upstreamRef`). Flip back to `true` to bring the form back unchanged.
 */
export const SHOW_UPSTREAM_FIELDS = false;

/**
 * The confirm-relay form (replaces the browser prompt). Three fields, one
 * required when shown (see SHOW_UPSTREAM_FIELDS): the upstream reference is the audit trail linking our record to
 * the bid that actually exists on the auction house's system. Amount and time
 * record what ACTUALLY stands upstream, which can legitimately differ from the
 * client's figure (the house's increment, a placement minutes later).
 *
 * Phase 15 (owner decision 2026-09-28) — «На аукціоні вже вища ставка»: an
 * option on the same dialog. The operator types what the real auction stands
 * at (X) and the API decides: the client's own auto-bid limit is above X →
 * the answer is a 409 this dialog turns into its RAISE step («У клієнта
 * автоставка до L CHF…», N prefilled, «Підняти до N і передати»); otherwise
 * the bid is outbid upstream and the dialog closes with the caller's toast.
 * The one-click default (`{}`) is untouched while the option is off.
 *
 * `onSubmit` may return a promise; a rejection carrying the limit refusal
 * ({@link upstreamLimitRefusal}) is the one error this dialog consumes.
 *
 * Exported for `useMirrorBid` (the bid page's «Оброблено», the overview's «Обробити»), which mirrors through this
 * same form rather than a fork of it.
 */
export function ConfirmRelayDialog({
  item,
  busy,
  onCancel,
  onSubmit,
}: {
  item: Pick<RelayControlTarget, 'bid'>;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (payload: ConfirmRelayPayload) => Promise<void> | void;
}) {
  const [form, setForm] = useState<ConfirmRelayForm>({
    upstreamRef: '',
    upstreamAmount: String(item.bid),
    upstreamPlacedAt: '',
  });
  const [error, setError] = useState<string | null>(null);
  /** The option «На аукціоні вже вища ставка» — off = the one-click confirm. */
  const [upstreamMode, setUpstreamMode] = useState(false);
  const [upstreamText, setUpstreamText] = useState('');
  /** Set once the API answered «у клієнта автоставка» — the raise step. */
  const [raise, setRaise] = useState<{ upstream: number; limit: number; text: string } | null>(
    null,
  );

  const update = (key: keyof ConfirmRelayForm) => (event: { target: { value: string } }) => {
    setError(null);
    setForm((previous) => ({ ...previous, [key]: event.target.value }));
  };

  const send = async (payload: ConfirmRelayPayload) => {
    try {
      await onSubmit(payload);
    } catch (failure) {
      const refusal = upstreamLimitRefusal(failure);
      if (!refusal || payload.upstreamHigherChf === undefined) {
        // Callers report their own failures; one that reaches here anyway is
        // said inside the dialog rather than lost as an unhandled rejection.
        setError(relayErrorMessage(failure));
        return;
      }
      setRaise({
        upstream: payload.upstreamHigherChf,
        limit: refusal.clientLimitChf,
        text: String(refusal.suggestedChf),
      });
      setError(refusal.again ? t('bids.relayQueue.errors.raiseNotAuthorised') : null);
    }
  };

  const raiseAmount = raise ? parseChf(raise.text) : null;

  const submit = () => {
    if (raise) {
      if (raiseAmount === null || raiseAmount <= raise.upstream || raiseAmount > raise.limit) {
        setError(
          t('bids.relayQueue.upstream.raiseInvalid', {
            upstream: formatAmount(raise.upstream),
            limit: formatAmount(raise.limit),
          }),
        );
        return;
      }
      void send({ upstreamHigherChf: raise.upstream, raiseToChf: raiseAmount });
      return;
    }
    if (upstreamMode) {
      const upstream = parseChf(upstreamText);
      if (upstream === null || upstream < item.bid) {
        setError(t('bids.relayQueue.upstream.upstreamInvalid', { bid: formatAmount(item.bid) }));
        return;
      }
      void send({ upstreamHigherChf: upstream });
      return;
    }
    if (!SHOW_UPSTREAM_FIELDS) {
      void send({});
      return;
    }
    const payload = buildConfirmRelayPayload(form);
    if (!payload) {
      setError(t('bids.relayQueue.confirmForm.refRequired'));
      return;
    }
    void send(payload);
  };

  const confirmLabel = raise
    ? t('bids.relayQueue.upstream.raiseSubmit', { amount: formatAmount(raiseAmount ?? 0) })
    : upstreamMode
      ? t('bids.relayQueue.upstream.submit')
      : t('bids.relayQueue.actions.submitConfirm');

  return (
    <ConfirmDialog
      open
      title={t('bids.relayQueue.confirmForm.title')}
      confirmLabel={confirmLabel}
      busy={busy}
      onCancel={onCancel}
      onConfirm={submit}
      message={
        <form
          data-testid="confirm-relay-form"
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          {raise ? (
            <>
              <p className="font-semibold text-cf-warn">
                {t('bids.relayQueue.upstream.clientLimit', { limit: formatAmount(raise.limit) })}
              </p>
              <Field label={t('bids.relayQueue.upstream.raiseAmount')}>
                <input
                  name="raiseToChf"
                  type="text"
                  inputMode="numeric"
                  autoFocus
                  value={raise.text}
                  onChange={(event) => {
                    setError(null);
                    const text = event.target.value;
                    setRaise((previous) => (previous ? { ...previous, text } : previous));
                  }}
                  className={cn(FIELD_CLASS, 'font-mono')}
                />
              </Field>
            </>
          ) : (
            <>
              <p>
                {t(
                  SHOW_UPSTREAM_FIELDS && !upstreamMode
                    ? 'bids.relayQueue.confirmForm.intro'
                    : 'bids.relayQueue.confirmForm.introSimple',
                )}
              </p>
              <button
                type="button"
                aria-pressed={upstreamMode}
                onClick={() => {
                  setError(null);
                  setUpstreamMode((previous) => !previous);
                }}
                className={cn(
                  'w-fit rounded-cf-md border px-3 py-1.5 text-xs font-semibold',
                  upstreamMode
                    ? 'border-cf-warn bg-cf-warn-tint text-cf-warn'
                    : 'border-cf-line text-cf-ink-muted',
                )}
              >
                {t('bids.relayQueue.upstream.toggle')}
              </button>
              {upstreamMode && (
                <Field label={t('bids.relayQueue.upstream.upstreamAmount')}>
                  <input
                    name="upstreamHigherChf"
                    type="text"
                    inputMode="numeric"
                    autoFocus
                    value={upstreamText}
                    onChange={(event) => {
                      setError(null);
                      setUpstreamText(event.target.value);
                    }}
                    className={cn(FIELD_CLASS, 'font-mono')}
                  />
                </Field>
              )}
              {SHOW_UPSTREAM_FIELDS && !upstreamMode && (
                <>
                  <Field label={t('bids.relayQueue.confirmForm.upstreamRef')}>
                    <input
                      name="upstreamRef"
                      type="text"
                      autoFocus
                      required
                      value={form.upstreamRef}
                      onChange={update('upstreamRef')}
                      className={FIELD_CLASS}
                    />
                  </Field>
                  <Field label={t('bids.relayQueue.confirmForm.upstreamAmount')}>
                    <input
                      name="upstreamAmount"
                      type="text"
                      inputMode="numeric"
                      value={form.upstreamAmount}
                      onChange={update('upstreamAmount')}
                      className={cn(FIELD_CLASS, 'font-mono')}
                    />
                  </Field>
                  <Field label={t('bids.relayQueue.confirmForm.upstreamPlacedAt')}>
                    <input
                      name="upstreamPlacedAt"
                      type="datetime-local"
                      value={form.upstreamPlacedAt}
                      onChange={update('upstreamPlacedAt')}
                      className={cn(FIELD_CLASS, 'font-mono')}
                    />
                  </Field>
                </>
              )}
            </>
          )}
          {error && (
            <p role="alert" className="text-xs text-cf-danger">
              {error}
            </p>
          )}
        </form>
      }
    />
  );
}

/** The select's initial choice — the most common refusal, and a member of the API's own vocabulary. */
const DEFAULT_REJECT_REASON: RelayRejectReason = 'amount_below_reserve';

/** The reject form: the reason select offers EXACTLY the contracts vocabulary; the note is optional (≤500). */
function RejectRelayDialog({
  busy,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  onCancel: () => void;
  onSubmit: (payload: RejectRelayPayload) => void;
}) {
  const [reason, setReason] = useState<RelayRejectReason>(DEFAULT_REJECT_REASON);
  const [note, setNote] = useState('');

  const submit = () => {
    const trimmed = note.trim();
    onSubmit(trimmed ? { reason, note: trimmed } : { reason });
  };

  return (
    <ConfirmDialog
      open
      tone="danger"
      title={t('bids.relayQueue.rejectForm.title')}
      confirmLabel={t('bids.relayQueue.actions.reject')}
      busy={busy}
      onCancel={onCancel}
      onConfirm={submit}
      message={
        <form
          data-testid="reject-relay-form"
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <p>{t('bids.relayQueue.rejectForm.intro')}</p>
          <Field label={t('bids.relayQueue.rejectForm.reason')}>
            <select
              name="reason"
              value={reason}
              onChange={(event) => setReason(event.target.value as RelayRejectReason)}
              className={FIELD_CLASS}
            >
              {RELAY_REJECT_REASONS.map((option) => (
                <option key={option} value={option}>
                  {t(`bids.relayQueue.rejectForm.reasons.${option}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('bids.relayQueue.rejectForm.note')}>
            <textarea
              name="note"
              maxLength={500}
              rows={3}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              className={FIELD_CLASS}
            />
          </Field>
        </form>
      }
    />
  );
}

const VARIANT_CLASS = {
  primary: 'border-cf-brand bg-cf-brand text-white hover:bg-cf-brand-hover',
  success: 'border-cf-success/40 bg-cf-surface text-cf-success hover:bg-cf-success-tint',
  danger: 'border-cf-danger/35 bg-cf-surface text-cf-danger hover:bg-cf-danger-tint',
  outline: 'border-cf-line bg-cf-surface text-cf-ink-muted hover:border-cf-ink',
} as const;

function ActionButton({
  onClick,
  disabled,
  variant,
  label,
  title,
}: {
  onClick: () => void;
  disabled: boolean;
  variant: keyof typeof VARIANT_CLASS;
  label: string;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'h-9 rounded-cf-md border px-2.5 text-xs font-semibold whitespace-nowrap disabled:opacity-50 lg:h-8',
        VARIANT_CLASS[variant],
      )}
    >
      {label}
    </button>
  );
}
